import { useState } from 'react'
import { useEditorStore, useSelectedBlock } from '../../store/editor.store'
import type {
  BlockLayout, BlockSize, BlockMargin,
  BlockTypography, BlockColors, BlockVisualStyles,
} from '../../store/editor.store'

// ── Helpers ───────────────────────────────────────────────────────────────────

type Tab = 'layout' | 'size' | 'spacing' | 'typography' | 'color' | 'border' | 'effects' | 'responsive'

const TABS: { id: Tab; label: string }[] = [
  { id: 'layout', label: 'Layout' },
  { id: 'size', label: 'Size' },
  { id: 'spacing', label: 'Spacing' },
  { id: 'typography', label: 'Typo' },
  { id: 'color', label: 'Color' },
  { id: 'border', label: 'Border' },
  { id: 'effects', label: 'Effects' },
  { id: 'responsive', label: 'Resp.' },
]

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
      <span style={{ width: 72, fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  )
}

function TextInput({
  value, onChange, placeholder, unit,
}: { value?: string | number; onChange: (v: string) => void; placeholder?: string; unit?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <input
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1, fontSize: 11, background: 'var(--bg-3)', border: '1px solid var(--border)',
          borderRadius: 4, padding: '3px 6px', color: 'var(--text-primary)', outline: 'none',
        }}
      />
      {unit && <span style={{ fontSize: 10, color: 'var(--text-muted)', width: 20 }}>{unit}</span>}
    </div>
  )
}

function NumInput({
  value, onChange, placeholder, min = 0,
}: { value?: number; onChange: (v: number | undefined) => void; placeholder?: string; min?: number }) {
  return (
    <input
      type="number"
      value={value ?? ''}
      min={min}
      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      placeholder={placeholder}
      style={{
        width: '100%', fontSize: 11, background: 'var(--bg-3)', border: '1px solid var(--border)',
        borderRadius: 4, padding: '3px 6px', color: 'var(--text-primary)', outline: 'none',
      }}
    />
  )
}

function SelectInput({
  value, options, onChange,
}: { value?: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%', fontSize: 11, background: 'var(--bg-3)', border: '1px solid var(--border)',
        borderRadius: 4, padding: '3px 6px', color: 'var(--text-primary)', outline: 'none',
      }}
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}

function ColorInput({ value, onChange }: { value?: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 28, height: 28, border: 'none', padding: 0, borderRadius: 4, cursor: 'pointer' }}
      />
      <TextInput value={value} onChange={onChange} placeholder="#000000 или token:color.primary" />
    </div>
  )
}

// ── Layout Panel ──────────────────────────────────────────────────────────────

function LayoutPanel({ layout, onChange }: { layout?: BlockLayout; onChange: (l: BlockLayout | undefined) => void }) {
  const L = layout ?? {}
  const upd = (patch: Partial<BlockLayout>) => onChange({ ...L, ...patch })
  const reset = () => onChange(undefined)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button onClick={reset} style={{ fontSize: 10, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Сбросить</button>
      </div>
      <Row label="Display">
        <SelectInput
          value={L.display}
          options={[
            { value: 'block', label: 'block' },
            { value: 'flex', label: 'flex' },
            { value: 'grid', label: 'grid' },
            { value: 'inline', label: 'inline' },
            { value: 'inline-block', label: 'inline-block' },
            { value: 'none', label: 'none' },
          ]}
          onChange={(v) => upd({ display: v as BlockLayout['display'] || undefined })}
        />
      </Row>
      {L.display === 'flex' && (
        <>
          <Row label="Direction">
            <SelectInput
              value={L.flexDirection}
              options={[
                { value: 'row', label: 'row' },
                { value: 'column', label: 'column' },
                { value: 'row-reverse', label: 'row-reverse' },
                { value: 'column-reverse', label: 'column-reverse' },
              ]}
              onChange={(v) => upd({ flexDirection: v as BlockLayout['flexDirection'] || undefined })}
            />
          </Row>
          <Row label="Wrap">
            <SelectInput
              value={L.flexWrap}
              options={[{ value: 'wrap', label: 'wrap' }, { value: 'nowrap', label: 'nowrap' }]}
              onChange={(v) => upd({ flexWrap: v as BlockLayout['flexWrap'] || undefined })}
            />
          </Row>
          <Row label="Align">
            <SelectInput
              value={L.alignItems}
              options={[
                { value: 'flex-start', label: 'flex-start' },
                { value: 'center', label: 'center' },
                { value: 'flex-end', label: 'flex-end' },
                { value: 'stretch', label: 'stretch' },
                { value: 'baseline', label: 'baseline' },
              ]}
              onChange={(v) => upd({ alignItems: v || undefined })}
            />
          </Row>
          <Row label="Justify">
            <SelectInput
              value={L.justifyContent}
              options={[
                { value: 'flex-start', label: 'flex-start' },
                { value: 'center', label: 'center' },
                { value: 'flex-end', label: 'flex-end' },
                { value: 'space-between', label: 'space-between' },
                { value: 'space-around', label: 'space-around' },
                { value: 'space-evenly', label: 'space-evenly' },
              ]}
              onChange={(v) => upd({ justifyContent: v || undefined })}
            />
          </Row>
          <Row label="Gap">
            <TextInput value={L.gap} onChange={(v) => upd({ gap: v || undefined })} placeholder="16px" />
          </Row>
        </>
      )}
      {L.display === 'grid' && (
        <>
          <Row label="Columns">
            <TextInput value={L.gridCols} onChange={(v) => upd({ gridCols: v || undefined })} placeholder="repeat(3, 1fr)" />
          </Row>
          <Row label="Rows">
            <TextInput value={L.gridRows} onChange={(v) => upd({ gridRows: v || undefined })} placeholder="auto" />
          </Row>
          <Row label="Gap">
            <TextInput value={L.gap} onChange={(v) => upd({ gap: v || undefined })} placeholder="16px" />
          </Row>
        </>
      )}
    </div>
  )
}

// ── Size Panel ────────────────────────────────────────────────────────────────

function SizePanel({ size, onChange }: { size?: BlockSize; onChange: (s: BlockSize | undefined) => void }) {
  const S = size ?? {}
  const upd = (patch: Partial<BlockSize>) => onChange({ ...S, ...patch })
  const reset = () => onChange(undefined)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button onClick={reset} style={{ fontSize: 10, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Сбросить</button>
      </div>
      <Row label="Width"><TextInput value={S.width} onChange={(v) => upd({ width: v || undefined })} placeholder="100%" /></Row>
      <Row label="Height"><TextInput value={S.height} onChange={(v) => upd({ height: v || undefined })} placeholder="auto" /></Row>
      <Row label="Min-W"><TextInput value={S.minWidth} onChange={(v) => upd({ minWidth: v || undefined })} placeholder="0" /></Row>
      <Row label="Max-W"><TextInput value={S.maxWidth} onChange={(v) => upd({ maxWidth: v || undefined })} placeholder="none" /></Row>
      <Row label="Min-H"><TextInput value={S.minHeight} onChange={(v) => upd({ minHeight: v || undefined })} placeholder="0" /></Row>
      <Row label="Max-H"><TextInput value={S.maxHeight} onChange={(v) => upd({ maxHeight: v || undefined })} placeholder="none" /></Row>
      <Row label="Aspect"><TextInput value={S.aspectRatio} onChange={(v) => upd({ aspectRatio: v || undefined })} placeholder="16/9" /></Row>
    </div>
  )
}

// ── Spacing Panel (padding + margin) ─────────────────────────────────────────

function SpacingPanel({
  paddingTop, paddingBottom, paddingLeft, paddingRight,
  margin, onPaddingChange, onMarginChange,
}: {
  paddingTop?: number; paddingBottom?: number; paddingLeft?: number; paddingRight?: number
  margin?: BlockMargin
  onPaddingChange: (key: 'wrapPaddingTop' | 'wrapPaddingBottom' | 'wrapPaddingLeft' | 'wrapPaddingRight', v: number | undefined) => void
  onMarginChange: (m: BlockMargin | undefined) => void
}) {
  const M = margin ?? {}
  const updM = (patch: Partial<BlockMargin>) => onMarginChange({ ...M, ...patch })

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: 'var(--text-muted)' }}>PADDING</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 12 }}>
        <Row label="Top"><NumInput value={paddingTop} onChange={(v) => onPaddingChange('wrapPaddingTop', v)} /></Row>
        <Row label="Bottom"><NumInput value={paddingBottom} onChange={(v) => onPaddingChange('wrapPaddingBottom', v)} /></Row>
        <Row label="Left"><NumInput value={paddingLeft} onChange={(v) => onPaddingChange('wrapPaddingLeft', v)} /></Row>
        <Row label="Right"><NumInput value={paddingRight} onChange={(v) => onPaddingChange('wrapPaddingRight', v)} /></Row>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: 'var(--text-muted)' }}>MARGIN</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        <Row label="Top"><NumInput value={M.top} onChange={(v) => updM({ top: v })} /></Row>
        <Row label="Bottom"><NumInput value={M.bottom} onChange={(v) => updM({ bottom: v })} /></Row>
        <Row label="Left"><NumInput value={M.left} onChange={(v) => updM({ left: v })} /></Row>
        <Row label="Right"><NumInput value={M.right} onChange={(v) => updM({ right: v })} /></Row>
      </div>
    </div>
  )
}

// ── Typography Panel ──────────────────────────────────────────────────────────

function TypographyPanel({ typo, onChange }: { typo?: BlockTypography; onChange: (t: BlockTypography | undefined) => void }) {
  const T = typo ?? {}
  const upd = (patch: Partial<BlockTypography>) => onChange({ ...T, ...patch })
  const reset = () => onChange(undefined)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button onClick={reset} style={{ fontSize: 10, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Сбросить</button>
      </div>
      <Row label="Font">
        <TextInput value={T.fontFamily} onChange={(v) => upd({ fontFamily: v || undefined })} placeholder="Inter, sans-serif" />
      </Row>
      <Row label="Weight">
        <SelectInput
          value={T.fontWeight}
          options={[
            { value: '300', label: 'Light 300' },
            { value: '400', label: 'Regular 400' },
            { value: '500', label: 'Medium 500' },
            { value: '600', label: 'SemiBold 600' },
            { value: '700', label: 'Bold 700' },
            { value: '900', label: 'Black 900' },
          ]}
          onChange={(v) => upd({ fontWeight: v || undefined })}
        />
      </Row>
      <Row label="H.Size">
        <TextInput value={T.headingSize} onChange={(v) => upd({ headingSize: v || undefined })} placeholder="48px" />
      </Row>
      <Row label="B.Size">
        <TextInput value={T.bodySize} onChange={(v) => upd({ bodySize: v || undefined })} placeholder="16px" />
      </Row>
      <Row label="Align">
        <SelectInput
          value={T.textAlign}
          options={[
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
          ]}
          onChange={(v) => upd({ textAlign: v || undefined })}
        />
      </Row>
      <Row label="Spacing">
        <TextInput value={T.letterSpacing} onChange={(v) => upd({ letterSpacing: v || undefined })} placeholder="0.05em" />
      </Row>
      <Row label="L.Height">
        <TextInput value={T.lineHeight} onChange={(v) => upd({ lineHeight: v || undefined })} placeholder="1.6" />
      </Row>
    </div>
  )
}

// ── Color Panel ───────────────────────────────────────────────────────────────

function ColorPanel({ colors, onChange }: { colors?: BlockColors; onChange: (c: BlockColors | undefined) => void }) {
  const C = colors ?? {}
  const upd = (patch: Partial<BlockColors>) => onChange({ ...C, ...patch })
  const reset = () => onChange(undefined)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button onClick={reset} style={{ fontSize: 10, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Сбросить</button>
      </div>
      <Row label="Text">
        <ColorInput value={C.textColor} onChange={(v) => upd({ textColor: v || undefined })} />
      </Row>
      <Row label="BG">
        <ColorInput value={C.bg} onChange={(v) => upd({ bg: v || undefined })} />
      </Row>
      <Row label="Accent">
        <ColorInput value={C.accentColor} onChange={(v) => upd({ accentColor: v || undefined })} />
      </Row>
    </div>
  )
}

// ── Border Panel ──────────────────────────────────────────────────────────────

function BorderPanel({ visual, onChange }: { visual?: BlockVisualStyles; onChange: (v: BlockVisualStyles | undefined) => void }) {
  const V = visual ?? {}
  const upd = (patch: Partial<BlockVisualStyles>) => onChange({ ...V, ...patch })
  const reset = () => onChange(undefined)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <button onClick={reset} style={{ fontSize: 10, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Сбросить</button>
      </div>
      <Row label="Radius">
        <TextInput value={V.borderRadius} onChange={(v) => upd({ borderRadius: v || undefined })} placeholder="8px" />
      </Row>
      <Row label="Width">
        <TextInput value={V.borderWidth} onChange={(v) => upd({ borderWidth: v || undefined })} placeholder="1px" />
      </Row>
      <Row label="Style">
        <SelectInput
          value={V.borderStyle}
          options={[
            { value: 'solid', label: 'solid' },
            { value: 'dashed', label: 'dashed' },
            { value: 'dotted', label: 'dotted' },
            { value: 'none', label: 'none' },
          ]}
          onChange={(v) => upd({ borderStyle: v || undefined })}
        />
      </Row>
      <Row label="Color">
        <ColorInput value={V.borderColor} onChange={(v) => upd({ borderColor: v || undefined })} />
      </Row>
      <Row label="Shadow">
        <TextInput value={V.boxShadow} onChange={(v) => upd({ boxShadow: v || undefined })} placeholder="0 4px 24px rgba(0,0,0,.12)" />
      </Row>
    </div>
  )
}

// ── Effects Panel ─────────────────────────────────────────────────────────────

function EffectsPanel({ visual, onChange }: { visual?: BlockVisualStyles; onChange: (v: BlockVisualStyles | undefined) => void }) {
  const V = visual ?? {}
  const upd = (patch: Partial<BlockVisualStyles>) => onChange({ ...V, ...patch })

  return (
    <div>
      <Row label="Opacity">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="range" min={0} max={1} step={0.01}
            value={V.opacity ?? 1}
            onChange={(e) => upd({ opacity: parseFloat(e.target.value) })}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: 11, width: 32, textAlign: 'right', color: 'var(--text-muted)' }}>
            {Math.round((V.opacity ?? 1) * 100)}%
          </span>
        </div>
      </Row>
      <Row label="Blur">
        <TextInput value={V.backdropFilter} onChange={(v) => upd({ backdropFilter: v || undefined })} placeholder="blur(16px)" />
      </Row>
      <Row label="Rotate">
        <TextInput value={V.rotate} onChange={(v) => upd({ rotate: v || undefined })} placeholder="3deg" />
      </Row>
      <Row label="Scale">
        <TextInput value={V.scale} onChange={(v) => upd({ scale: v || undefined })} placeholder="1.05" />
      </Row>
      <Row label="Blend">
        <SelectInput
          value={V.mixBlendMode}
          options={[
            { value: 'normal', label: 'normal' },
            { value: 'multiply', label: 'multiply' },
            { value: 'overlay', label: 'overlay' },
            { value: 'screen', label: 'screen' },
            { value: 'hard-light', label: 'hard-light' },
            { value: 'soft-light', label: 'soft-light' },
            { value: 'color-dodge', label: 'color-dodge' },
            { value: 'color-burn', label: 'color-burn' },
          ]}
          onChange={(v) => upd({ mixBlendMode: v || undefined })}
        />
      </Row>
      <Row label="Overflow">
        <SelectInput
          value={V.overflow}
          options={[
            { value: 'visible', label: 'visible' },
            { value: 'hidden', label: 'hidden' },
            { value: 'auto', label: 'auto' },
          ]}
          onChange={(v) => upd({ overflow: (v as BlockVisualStyles['overflow']) || undefined })}
        />
      </Row>
    </div>
  )
}

// ── Responsive Panel ──────────────────────────────────────────────────────────

function ResponsivePanel({
  responsive, onChange,
}: {
  responsive?: import('../../store/editor.store').BlockResponsiveStyles
  onChange: (r: import('../../store/editor.store').BlockResponsiveStyles | undefined) => void
}) {
  type BpKey = 'tablet' | 'mobile'
  const R = responsive ?? {}
  const [activeBp, setActiveBp] = useState<BpKey>('mobile')
  const override = R[activeBp] ?? {}
  const upd = (patch: Partial<typeof override>) => onChange({ ...R, [activeBp]: { ...override, ...patch } })

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        {(['tablet', 'mobile'] as BpKey[]).map((bp) => (
          <button
            key={bp}
            onClick={() => setActiveBp(bp)}
            style={{
              flex: 1, fontSize: 11, padding: '4px', border: '1px solid var(--border)',
              borderRadius: 4, cursor: 'pointer',
              background: activeBp === bp ? 'var(--accent)' : 'var(--bg-3)',
              color: activeBp === bp ? '#fff' : 'var(--text-primary)',
            }}
          >{bp}</button>
        ))}
      </div>
      <Row label="Hide">
        <input
          type="checkbox"
          checked={override.hideBlock === true}
          onChange={(e) => upd({ hideBlock: e.target.checked ? true : undefined })}
        />
      </Row>
      <Row label="Pad-T"><NumInput value={override.wrapPaddingTop} onChange={(v) => upd({ wrapPaddingTop: v })} /></Row>
      <Row label="Pad-B"><NumInput value={override.wrapPaddingBottom} onChange={(v) => upd({ wrapPaddingBottom: v })} /></Row>
      <Row label="H.Size">
        <TextInput value={override.headingSize} onChange={(v) => upd({ headingSize: v || undefined })} placeholder="32px" />
      </Row>
      <Row label="B.Size">
        <TextInput value={override.bodySize} onChange={(v) => upd({ bodySize: v || undefined })} placeholder="14px" />
      </Row>
      <Row label="Min-H">
        <NumInput value={override.blockMinHeight} onChange={(v) => upd({ blockMinHeight: v })} />
      </Row>
    </div>
  )
}

// ── Main Visual Inspector ─────────────────────────────────────────────────────

export function VisualInspector() {
  const [tab, setTab] = useState<Tab>('layout')
  const selection = useSelectedBlock()
  const {
    setBlockLayout, setBlockSize, setBlockWrapper, setBlockTypography,
    setBlockColors, setBlockMargin,
  } = useEditorStore()

  if (!selection) {
    return (
      <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
        Выберите блок для редактирования стилей
      </div>
    )
  }

  const { block, sectionId } = selection

  // Handlers
  const onLayout = (l: BlockLayout | undefined) => setBlockLayout(sectionId, block.id, l)
  const onSize = (s: BlockSize | undefined) => setBlockSize(sectionId, block.id, s)
  const onTypo = (t: import('../../store/editor.store').BlockTypography | undefined) => setBlockTypography(sectionId, block.id, t)
  const onColors = (c: import('../../store/editor.store').BlockColors | undefined) => setBlockColors(sectionId, block.id, c)
  const onVisual = (v: import('../../store/editor.store').BlockVisualStyles | undefined) => {
    setBlockWrapper(sectionId, block.id, { blockVisualStyles: v })
  }
  const onPadding = (key: 'wrapPaddingTop' | 'wrapPaddingBottom' | 'wrapPaddingLeft' | 'wrapPaddingRight', v: number | undefined) => {
    setBlockWrapper(sectionId, block.id, { [key]: v })
  }
  const onMargin = (m: BlockMargin | undefined) => setBlockMargin(sectionId, block.id, m)
  const onResponsive = (r: import('../../store/editor.store').BlockResponsiveStyles | undefined) => {
    setBlockWrapper(sectionId, block.id, { blockResponsiveStyles: r })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', overflowX: 'auto', flexShrink: 0 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '6px 8px', fontSize: 10, fontWeight: tab === t.id ? 700 : 400,
              border: 'none', borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              background: 'none', cursor: 'pointer', color: tab === t.id ? 'var(--accent)' : 'var(--text-muted)',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Panel */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>
        {tab === 'layout' && (
          <LayoutPanel layout={block.blockLayout} onChange={onLayout} />
        )}
        {tab === 'size' && (
          <SizePanel size={block.blockSize} onChange={onSize} />
        )}
        {tab === 'spacing' && (
          <SpacingPanel
            paddingTop={block.wrapPaddingTop}
            paddingBottom={block.wrapPaddingBottom}
            paddingLeft={block.wrapPaddingLeft}
            paddingRight={block.wrapPaddingRight}
            margin={block.blockMargin}
            onPaddingChange={onPadding}
            onMarginChange={onMargin}
          />
        )}
        {tab === 'typography' && (
          <TypographyPanel typo={block.blockTypography} onChange={onTypo} />
        )}
        {tab === 'color' && (
          <ColorPanel colors={block.blockColors} onChange={onColors} />
        )}
        {tab === 'border' && (
          <BorderPanel visual={block.blockVisualStyles} onChange={onVisual} />
        )}
        {tab === 'effects' && (
          <EffectsPanel visual={block.blockVisualStyles} onChange={onVisual} />
        )}
        {tab === 'responsive' && (
          <ResponsivePanel responsive={block.blockResponsiveStyles} onChange={onResponsive} />
        )}
      </div>
    </div>
  )
}
